/** @format */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Tooltip,
    IconButton,
    Select,
    MenuItem as MuiMenuItem,
    FormControl,
} from "@mui/material";
import {
    NotificationsActive,
    CheckCircleOutline,
    OpenInNew,
    Refresh,
    PlayArrow,
    SyncAlt,
    TrendingUp,
    TrendingDown,
    AccessAlarm,
    Handshake,
    AccountBalanceWallet,
    WarningAmber,
    Insights,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchMyBusinesses,
    fetchQuickBooksStatus,
    fetchArAging,
    fetchInvoices,
    fetchDunningEvents,
    fetchBusinessPromises,
    fetchCollectionsMetrics,
    runDunningNow,
    syncAll,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusiness,
    selectActiveBusinessId,
    selectActiveBusinessRole,
    selectBusinessMemberships,
    selectIsLoadingBusinesses,
    selectQuickBooks,
    selectIsLoadingQuickBooks,
    selectIsQuickBooksConnected,
    selectArAging,
    selectIsLoadingArAging,
    selectInvoices,
    selectIsLoadingInvoices,
    selectDunningEvents,
    selectIsLoadingDunningEvents,
    selectIsRunningDunning,
    selectLastDunningRun,
    selectIsSyncing,
    selectBusinessPromises,
    selectIsLoadingBusinessPromises,
    selectCollectionsMetrics,
    selectIsLoadingCollectionsMetrics,
    selectCollectionsMetricsError,
} from "../../redux/selectors/collectSelector";
import { selectCollectAccess } from "../../redux/selectors/collectBillingSelector";
import { colors } from "../../utility/types/constant";
import { CollectionsRange, KpiBreakdownMetric } from "../../utility/types/collectTypes";
import AutopilotSummaryPanel from "./dashboard/AutopilotSummaryPanel";
import ActionCenterPanel from "./dashboard/ActionCenterPanel";
import RecoveryImpactPanel from "./dashboard/RecoveryImpactPanel";
import ActivityFeedPanel from "./dashboard/ActivityFeedPanel";
import KpiDetailModal from "./dashboard/KpiDetailModal";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectBadge,
    CollectHeroTitle,
    CollectHeroSubtitle,
    CollectHeroActions,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectKpiCard,
    CollectKpiLabel,
    CollectKpiValue,
    CollectKpiHint,
    CollectPrimaryButton,
    CollectSecondaryButton,
    CollectGhostButton,
    CollectStatusChip,
    CollectInfoBanner,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    AgingBucketCard,
    AgingBucketLabel,
    AgingBucketValue,
    AgingBucketCount,
} from "./styles";
import OnboardingChecklist from "./OnboardingChecklist";
import { useToast } from "../../providers/ToastProvider";

// ─────────────────────────────────────────────────────────────────────────
// Collect Dashboard — entry point for the AARRP module.
// Shows:
//   • Tenant context (active business + QuickBooks connection state)
//   • Top-line KPIs (outstanding A/R, open invoices, reminders sent)
//   • A/R aging snapshot
//   • Recent invoices + recent reminder activity
//   • Run-now action for owners/admins
// ─────────────────────────────────────────────────────────────────────────

function formatCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount || 0);
    } catch {
        return `$${(amount || 0).toFixed(2)}`;
    }
}

function formatDate(value?: string): string {
    if (!value) return "—";
    try {
        // QBO date-only fields come back as ISO strings at UTC midnight
        // (e.g. "2026-04-14T00:00:00.000Z"). If we render with the user's
        // local timezone, anyone west of UTC sees the previous day.
        // Force UTC interpretation so the displayed date matches what's
        // shown in QuickBooks.
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
        });
    } catch {
        return value;
    }
}

function timeAgo(value?: string): string {
    if (!value) return "—";
    const diff = Date.now() - new Date(value).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(value);
}

const AGING_LABELS: Record<string, string> = {
    current: "Not due yet",
    "1_30": "1–30 days overdue",
    "31_60": "31–60 days overdue",
    "61_90": "61–90 days overdue",
    "90_plus": "90+ days overdue",
};

const AGING_DESCRIPTIONS: Record<string, string> = {
    current: "Owed but not yet due",
    "1_30": "Recently overdue. Send a friendly nudge",
    "31_60": "Overdue. Send a firm reminder",
    "61_90": "Seriously overdue. Escalate",
    "90_plus": "Severely overdue, consider write-off",
};

const AGING_TONE: Record<string, "ok" | "warn" | "bad"> = {
    current: "ok",
    "1_30": "warn",
    "31_60": "warn",
    "61_90": "bad",
    "90_plus": "bad",
};

const CollectDashboard: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();

    const activeBusiness = useSelector(selectActiveBusiness);
    const activeBusinessId = useSelector(selectActiveBusinessId);
    const activeRole = useSelector(selectActiveBusinessRole);
    const memberships = useSelector(selectBusinessMemberships);
    const isLoadingBusinesses = useSelector(selectIsLoadingBusinesses);

    const quickbooks = useSelector(selectQuickBooks);
    const isLoadingQuickBooks = useSelector(selectIsLoadingQuickBooks);
    const isConnected = useSelector(selectIsQuickBooksConnected);

    const arAging = useSelector(selectArAging);
    const isLoadingArAging = useSelector(selectIsLoadingArAging);

    const invoices = useSelector(selectInvoices);
    const isLoadingInvoices = useSelector(selectIsLoadingInvoices);

    const dunningEvents = useSelector(selectDunningEvents);
    const isLoadingDunningEvents = useSelector(selectIsLoadingDunningEvents);

    const isRunningDunning = useSelector(selectIsRunningDunning);
    const lastRun = useSelector(selectLastDunningRun);

    const isSyncing = useSelector(selectIsSyncing);

    const businessPromises = useSelector(selectBusinessPromises);
    const isLoadingBusinessPromises = useSelector(selectIsLoadingBusinessPromises);

    const collectionsMetrics = useSelector(selectCollectionsMetrics);
    const isLoadingCollectionsMetrics = useSelector(selectIsLoadingCollectionsMetrics);
    const collectionsMetricsError = useSelector(selectCollectionsMetricsError);

    // ── Range picker for collections metrics ──────────────────────────────
    const [metricsRange, setMetricsRange] = useState<CollectionsRange>("4w");
    // ── Which KPI card's drill-down modal is open (null = closed) ─────────
    const [activeKpi, setActiveKpi] = useState<KpiBreakdownMetric | null>(null);

    // Initial load — businesses (and via fulfilled-case, set active id)
    useEffect(() => {
        if (memberships.length === 0 && !isLoadingBusinesses) {
            dispatch(fetchMyBusinesses());
        }
    }, [dispatch, memberships.length, isLoadingBusinesses]);

    // Tenant-scoped loads — fire as soon as we have an active business
    useEffect(() => {
        if (!activeBusinessId) return;
        dispatch(fetchQuickBooksStatus(undefined));
        dispatch(fetchArAging(undefined));
        dispatch(fetchInvoices({ limit: 5 }));
        dispatch(fetchDunningEvents({ limit: 25 }));
        dispatch(fetchBusinessPromises({ status: "pending", limit: 25 }));
    }, [dispatch, activeBusinessId]);

    // Re-fetch metrics whenever the range dropdown changes (and on first mount)
    useEffect(() => {
        if (!activeBusinessId) return;
        dispatch(fetchCollectionsMetrics({ range: metricsRange }));
    }, [dispatch, activeBusinessId, metricsRange]);

    const canWrite = activeRole === "owner" || activeRole === "admin";
    // Prefer whichever tenant-scoped source has resolved a real display
    // currency (e.g. CAD for a Canadian QBO tenant). AR-aging and collections
    // metrics both carry it, so the Outstanding A/R card stays in sync with the
    // rest of the dashboard instead of defaulting to USD.
    const currency =
        arAging?.currency ||
        collectionsMetrics?.currency ||
        "USD";

    // ── Subscription access gate ─────────────────────────────────────────
    // When a business hasn't successfully paid its monthly subscription (real
    // lapse → hasAccess=false), the dashboard runs in a "limited access" mode:
    // only the Outstanding A/R and Overdue KPI cards stay readable, every other
    // card is blurred + non-clickable, and all other panels are hidden until
    // they settle up. Super admins are never gated.
    const collectAccess = useSelector(selectCollectAccess);
    const isLimitedAccess =
        !!collectAccess &&
        !collectAccess.isSuperAdmin &&
        !collectAccess.hasAccess;

    // Blur + block interaction styling for the locked (non-teaser) KPI cards.
    const lockedCardSx = {
        filter: "blur(12px)",
        pointerEvents: "none",
        userSelect: "none",
        opacity: 0.6,
    } as const;

    // Shared affordance for the clickable KPI cards — subtle lift on hover so
    // owners know they can drill into the underlying invoices/promises.
    const clickableKpiSx = {
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",
        },
    } as const;

    const onConnectQuickBooks = () => {
        navigate("/collect/connect");
    };

    const onRunDunning = async () => {
        const result: any = await dispatch(runDunningNow(undefined));
        if (result?.meta?.requestStatus === "fulfilled") {
            const r = result.payload || {};
            toast.success(
                `Dunning run complete — ${r.remindersSent ?? 0} sent, ${r.remindersSkipped ?? 0} skipped${r.remindersFailed ? `, ${r.remindersFailed} failed` : ""}.`,
            );
        } else {
            toast.error("Couldn't run dunning. Try again in a moment.");
        }
        // Refresh activity after a run completes
        dispatch(fetchDunningEvents({ limit: 25 }));
        dispatch(fetchArAging(undefined));
        dispatch(fetchBusinessPromises({ status: "pending", limit: 25 }));
        dispatch(fetchCollectionsMetrics({ range: metricsRange }));
    };

    const onSync = async () => {
        const result: any = await dispatch(syncAll(undefined));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("QuickBooks data synced.");
        } else {
            toast.error("Sync failed. Check your QuickBooks connection.");
        }
        // Refresh AR + invoices after sync completes
        dispatch(fetchArAging(undefined));
        dispatch(fetchInvoices({ limit: 5 }));
        dispatch(fetchCollectionsMetrics({ range: metricsRange }));
    };

    // ── Loading shell while we discover the business ─────────────────────
    if (isLoadingBusinesses && !activeBusinessId) {
        return (
            <CollectPageWrapper>
                <CollectHeroBanner>
                    <CollectHeroInner>
                        <CollectBadge label="AARRP · Collect" />
                        <CollectHeroTitle>Loading your workspace…</CollectHeroTitle>
                    </CollectHeroInner>
                </CollectHeroBanner>
                <CollectContentArea>
                    <CollectSectionCard>
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress sx={{ color: colors.primary }} />
                        </Box>
                    </CollectSectionCard>
                </CollectContentArea>
            </CollectPageWrapper>
        );
    }

    // ── No business yet ──────────────────────────────────────────────────
    if (!activeBusinessId) {
        return (
            <CollectPageWrapper>
                <CollectHeroBanner>
                    <CollectHeroInner>
                        <CollectBadge label="AARRP · Collect" />
                        <CollectHeroTitle>Welcome to FundsyFi Recover</CollectHeroTitle>
                        <CollectHeroSubtitle>
                            Stop chasing late payments. Connect QuickBooks, automate reminders,
                            and let your customers pay you faster without the awkward calls.
                        </CollectHeroSubtitle>
                        <CollectHeroActions>
                            <CollectPrimaryButton onClick={() => navigate("/collect/setup")}>
                                Create your workspace
                            </CollectPrimaryButton>
                        </CollectHeroActions>
                    </CollectHeroInner>
                </CollectHeroBanner>
            </CollectPageWrapper>
        );
    }

    // ── QuickBooks status refresh shell ────────────────────────────────
    // Prevents a temporary "not connected" flash while status is refetching
    // after navigating back to dashboard.
    if (isLoadingQuickBooks && !isConnected) {
        return (
            <CollectPageWrapper>
                <CollectContentArea
                    sx={{
                        marginTop: {
                            xs: "16px !important",
                            sm: "24px !important",
                            md: "32px !important",
                        },
                    }}
                >
                    <CollectSectionCard>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: { xs: 6, sm: 8 },
                                gap: 1.25,
                            }}
                        >
                            <CircularProgress sx={{ color: colors.primary }} />
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 600,
                                    color: "#475569",
                                }}
                            >
                                Syncing...
                            </Typography>
                        </Box>
                    </CollectSectionCard>
                </CollectContentArea>
            </CollectPageWrapper>
        );
    }

    return (
        <CollectPageWrapper>
            {/* ─── Action buttons (hidden in limited-access mode) ────── */}
            {!isLimitedAccess && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "12px",
                        padding: { xs: "16px 12px 0", sm: "24px 16px 0", md: "32px 24px 0" },
                    }}
                >
                    {canWrite && isConnected && (
                        <CollectPrimaryButton
                            startIcon={
                                isSyncing ? (
                                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                                ) : (
                                    <SyncAlt />
                                )
                            }
                            onClick={onSync}
                            disabled={isSyncing}
                        >
                            {isSyncing ? "Syncing…" : "Sync now"}
                        </CollectPrimaryButton>
                    )}
                    {canWrite && isConnected && (
                        <CollectSecondaryButton
                            sx={{
                                backgroundColor: "#ffffff",
                                color: colors.primary,
                                border: `1px solid ${colors.primary}33`,
                                "&:hover": {
                                    backgroundColor: `${colors.primary}08`,
                                    border: `1px solid ${colors.primary}66`,
                                },
                            }}
                            startIcon={
                                isRunningDunning ? (
                                    <CircularProgress size={16} sx={{ color: colors.primary }} />
                                ) : (
                                    <PlayArrow />
                                )
                            }
                            onClick={onRunDunning}
                            disabled={isRunningDunning}
                        >
                            {isRunningDunning ? "Sending reminders…" : "Run dunning now"}
                        </CollectSecondaryButton>
                    )}
                    <CollectSecondaryButton
                        sx={{
                            backgroundColor: "#ffffff",
                            color: colors.primary,
                            border: `1px solid ${colors.primary}33`,
                            "&:hover": {
                                backgroundColor: `${colors.primary}08`,
                                border: `1px solid ${colors.primary}66`,
                            },
                        }}
                        onClick={() => navigate("/collect/invoices")}
                    >
                        View all invoices
                    </CollectSecondaryButton>
                </Box>
            )}

            <CollectContentArea
                sx={{
                    marginTop: {
                        xs: "16px !important",
                        sm: "24px !important",
                        md: "32px !important",
                    },
                }}
            >
                <OnboardingChecklist />
                {collectionsMetricsError && (
                    <CollectInfoBanner
                        tone="warn"
                        sx={{ marginBottom: { xs: "16px", sm: "24px" } }}
                    >
                        <WarningAmber />
                        <Box flex={1}>
                            <strong>Collections data is unavailable.</strong>{" "}
                            {collectionsMetricsError}
                        </Box>
                    </CollectInfoBanner>
                )}
                {/* ─── Connection banner ──────────────────────────────── */}
                {!isConnected && (
                    <CollectInfoBanner
                        tone="warn"
                        sx={{
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "stretch", sm: "center" },
                            gap: { xs: 1.5, sm: 2 },
                            padding: { xs: "16px", sm: "18px 22px" },
                            marginBottom: { xs: "16px", sm: "24px" },
                        }}
                    >
                        <Box flex={1} sx={{ lineHeight: 1.55 }} color={colors.infoDarkBlue}>
                            <strong>QuickBooks isn't connected yet.</strong> Connect your
                            QuickBooks Online company to start syncing invoices, customers,
                            and AR balances.
                        </Box>
                        {canWrite && (
                            <CollectPrimaryButton
                                onClick={onConnectQuickBooks}
                                sx={{
                                    flexShrink: 0,
                                    width: { xs: "100%", sm: "auto" },
                                    minWidth: { sm: 200 },
                                }}
                            >
                                Connect QuickBooks
                            </CollectPrimaryButton>
                        )}
                    </CollectInfoBanner>
                )}
                {lastRun && (
                    <CollectInfoBanner
                        tone="ok"
                        sx={{
                            marginBottom: { xs: "16px", sm: "24px" },
                        }}
                    >
                        <CheckCircleOutline />
                        <Box flex={1}>
                            Dunning run complete · processed {lastRun.processed}, sent{" "}
                            {lastRun.sent}, skipped {lastRun.skipped}, failed {lastRun.failed}.
                        </Box>
                    </CollectInfoBanner>
                )}

                {/* ─── KPIs ───────────────────────────────────────────── */}
                {/* Range selector + tip are hidden in limited-access mode */}
                {!isLimitedAccess && (
                    <>
                        {/* Range selector sits above the KPI row, right-aligned */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                mb: 1.5,
                                gap: 1,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                }}
                            >
                                Period:
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={metricsRange}
                                    onChange={(e) =>
                                        setMetricsRange(e.target.value as CollectionsRange)
                                    }
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "0.82rem",
                                        fontWeight: 600,
                                        borderRadius: "10px",
                                        "& .MuiSelect-select": { py: 0.85 },
                                    }}
                                >
                                    <MuiMenuItem value="2w">Last 2 weeks</MuiMenuItem>
                                    <MuiMenuItem value="4w">Last 4 weeks</MuiMenuItem>
                                    <MuiMenuItem value="3m">Last 3 months</MuiMenuItem>
                                    <MuiMenuItem value="6m">Last 6 months</MuiMenuItem>
                                    <MuiMenuItem value="12m">Last 12 months</MuiMenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.74rem",
                                color: "#9ca3af",
                                mb: 1,
                            }}
                        >
                            Tip: click a card below to see the invoices, customers and
                            next actions behind the number.
                        </Typography>
                    </>
                )}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* 1 ── Outstanding A/R (total owed right now) */}
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <CollectKpiCard
                            accent={colors.primary}
                            onClick={isLimitedAccess ? undefined : () => setActiveKpi("outstanding")}
                            role={isLimitedAccess ? undefined : "button"}
                            tabIndex={isLimitedAccess ? -1 : 0}
                            sx={isLimitedAccess ? { cursor: "default" } : clickableKpiSx}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                                <AccountBalanceWallet sx={{ fontSize: 16, color: colors.primary }} />
                                <CollectKpiLabel sx={{ mb: "0 !important" }}>
                                    Outstanding A/R
                                </CollectKpiLabel>
                            </Box>
                            <CollectKpiValue>
                                {isLoadingArAging ? (
                                    <CircularProgress size={20} sx={{ color: colors.primary }} />
                                ) : (
                                    formatCurrency(arAging?.totalOutstanding ?? 0, currency)
                                )}
                            </CollectKpiValue>
                            <CollectKpiHint>
                                {arAging?.invoiceCount ?? 0} open invoice
                                {(arAging?.invoiceCount ?? 0) === 1 ? "" : "s"} total
                            </CollectKpiHint>
                            {/* Overdue % badge */}
                            {!isLoadingArAging && (arAging?.totalOutstanding ?? 0) > 0 && (() => {
                                const overdueTot = (arAging?.buckets ?? [])
                                    .filter((b) => b.label !== "current")
                                    .reduce((s, b) => s + (b.total || 0), 0);
                                const pct = Math.round((overdueTot / (arAging!.totalOutstanding || 1)) * 100);
                                return (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                                        <WarningAmber sx={{ fontSize: 13, color: pct > 50 ? "#dc2626" : "#f59e0b" }} />
                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", fontWeight: 700, color: pct > 50 ? "#dc2626" : "#f59e0b" }}>
                                            {pct}% overdue
                                        </Typography>
                                    </Box>
                                );
                            })()}
                        </CollectKpiCard>
                    </Grid>

                    {/* 2 ── Collected in the selected period */}
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <CollectKpiCard
                            accent={colors.mainSecondary}
                            onClick={isLimitedAccess ? undefined : () => setActiveKpi("collected")}
                            role={isLimitedAccess ? undefined : "button"}
                            tabIndex={isLimitedAccess ? -1 : 0}
                            sx={isLimitedAccess ? lockedCardSx : clickableKpiSx}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                                <TrendingUp sx={{ fontSize: 16, color: colors.mainSecondary }} />
                                <CollectKpiLabel sx={{ mb: "0 !important" }}>
                                    Collected
                                </CollectKpiLabel>
                            </Box>
                            <CollectKpiValue>
                                {collectionsMetricsError ? (
                                    "Unavailable"
                                ) : isLoadingCollectionsMetrics ? (
                                    <CircularProgress size={20} sx={{ color: colors.primary }} />
                                ) : (
                                    formatCurrency(
                                        collectionsMetrics?.collected?.amount ?? 0,
                                        collectionsMetrics?.currency ?? currency,
                                    )
                                )}
                            </CollectKpiValue>
                            <CollectKpiHint>
                                {collectionsMetricsError ? (
                                    "QBO source could not be verified"
                                ) : (
                                    <>
                                        {collectionsMetrics?.collected?.count ?? 0} invoice
                                        {(collectionsMetrics?.collected?.count ?? 0) === 1 ? "" : "s"}{" "}
                                        with payments · {collectionsMetrics?.rangeLabel ?? "Selected period"}
                                    </>
                                )}
                            </CollectKpiHint>
                            {/* Current-month context remains visible beneath the selected range. */}
                            {!isLoadingCollectionsMetrics && collectionsMetrics && (() => {
                                const monthAmt = collectionsMetrics.monthCollected?.amount ?? 0;
                                return (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                                        <TrendingUp sx={{ fontSize: 13, color: colors.mainSecondary }} />
                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", fontWeight: 700, color: colors.mainSecondary }}>
                                            {formatCurrency(monthAmt, collectionsMetrics.currency ?? currency)} this month
                                        </Typography>
                                    </Box>
                                );
                            })()}
                        </CollectKpiCard>
                    </Grid>

                    {/* 3 ── Overdue (total past due) */}
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <CollectKpiCard
                            accent="#dc2626"
                            onClick={isLimitedAccess ? undefined : () => setActiveKpi("overdue")}
                            role={isLimitedAccess ? undefined : "button"}
                            tabIndex={isLimitedAccess ? -1 : 0}
                            sx={isLimitedAccess ? { cursor: "default" } : clickableKpiSx}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                                <AccessAlarm sx={{ fontSize: 16, color: "#dc2626" }} />
                                <CollectKpiLabel sx={{ mb: "0 !important" }}>
                                    Overdue (total)
                                </CollectKpiLabel>
                            </Box>
                            <CollectKpiValue>
                                {collectionsMetricsError ? (
                                    "Unavailable"
                                ) : isLoadingCollectionsMetrics ? (
                                    <CircularProgress size={20} sx={{ color: colors.primary }} />
                                ) : (
                                    formatCurrency(
                                        (collectionsMetrics?.overdue?.amount
                                            ?? collectionsMetrics?.overdueCurrentMonth?.amount) ?? 0,
                                        collectionsMetrics?.currency ?? currency,
                                    )
                                )}
                            </CollectKpiValue>
                            <CollectKpiHint>
                                {collectionsMetricsError ? (
                                    "QBO source could not be verified"
                                ) : (
                                    <>
                                        {(collectionsMetrics?.overdue?.count
                                            ?? collectionsMetrics?.overdueCurrentMonth?.count) ?? 0} past-due invoice
                                        {((collectionsMetrics?.overdue?.count
                                            ?? collectionsMetrics?.overdueCurrentMonth?.count) ?? 0) === 1 ? "" : "s"}
                                    </>
                                )}
                            </CollectKpiHint>
                            {/* % of total outstanding */}
                            {!isLoadingCollectionsMetrics && collectionsMetrics && (() => {
                                const outstanding = collectionsMetrics.outstanding?.amount ?? 0;
                                const overdue = (collectionsMetrics.overdue?.amount
                                    ?? collectionsMetrics.overdueCurrentMonth?.amount) ?? 0;
                                const pct = outstanding > 0 ? Math.round((overdue / outstanding) * 100) : 0;
                                return (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                                        <WarningAmber sx={{ fontSize: 13, color: pct > 40 ? "#dc2626" : "#f59e0b" }} />
                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", fontWeight: 700, color: pct > 40 ? "#dc2626" : "#f59e0b" }}>
                                            {pct}% of total A/R
                                        </Typography>
                                    </Box>
                                );
                            })()}
                        </CollectKpiCard>
                    </Grid>

                    {/* 4 ── Promise-to-Pay with urgency split */}
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <CollectKpiCard
                            accent={colors.blueRibbon}
                            onClick={isLimitedAccess ? undefined : () => setActiveKpi("promises")}
                            role={isLimitedAccess ? undefined : "button"}
                            tabIndex={isLimitedAccess ? -1 : 0}
                            sx={isLimitedAccess ? lockedCardSx : clickableKpiSx}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                                <Handshake sx={{ fontSize: 16, color: colors.blueRibbon }} />
                                <CollectKpiLabel sx={{ mb: "0 !important" }}>
                                    Promise-to-Pay
                                </CollectKpiLabel>
                            </Box>
                            <CollectKpiValue>
                                {collectionsMetricsError ? (
                                    "Unavailable"
                                ) : isLoadingCollectionsMetrics ? (
                                    <CircularProgress size={20} sx={{ color: colors.primary }} />
                                ) : (
                                    formatCurrency(
                                        collectionsMetrics?.promises?.amount ?? 0,
                                        collectionsMetrics?.currency ?? currency,
                                    )
                                )}
                            </CollectKpiValue>
                            <CollectKpiHint>
                                {collectionsMetricsError ? (
                                    "QBO source could not be verified"
                                ) : (
                                    <>
                                        {collectionsMetrics?.promises?.count ??
                                            businessPromises.filter((p) => p.status === "pending").length}{" "}
                                        pending · expected payment
                                    </>
                                )}
                            </CollectKpiHint>
                            {/* Urgency: overdue vs upcoming promises */}
                            {!isLoadingBusinessPromises && businessPromises.length > 0 && (() => {
                                const today = new Date();
                                const todayMs = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
                                const overduePtp = businessPromises.filter(
                                    (p) => p.status === "pending" && new Date(p.promisedDate).getTime() < todayMs
                                ).length;
                                const upcomingPtp = businessPromises.filter(
                                    (p) => p.status === "pending" && new Date(p.promisedDate).getTime() >= todayMs
                                ).length;
                                return (
                                    <Box sx={{ display: "flex", gap: 0.75, mt: 0.75, flexWrap: "wrap" }}>
                                        {overduePtp > 0 && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, backgroundColor: "rgba(220,38,38,0.1)", borderRadius: "6px", px: 0.75, py: 0.25 }}>
                                                <WarningAmber sx={{ fontSize: 11, color: "#dc2626" }} />
                                                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.68rem", fontWeight: 700, color: "#dc2626" }}>
                                                    {overduePtp} overdue
                                                </Typography>
                                            </Box>
                                        )}
                                        {upcomingPtp > 0 && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, backgroundColor: "rgba(59,130,246,0.1)", borderRadius: "6px", px: 0.75, py: 0.25 }}>
                                                <TrendingUp sx={{ fontSize: 11, color: colors.blueRibbon }} />
                                                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.68rem", fontWeight: 700, color: colors.blueRibbon }}>
                                                    {upcomingPtp} upcoming
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })()}
                        </CollectKpiCard>
                    </Grid>

                    {/* 5 ── Reminders sent with success rate */}
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <CollectKpiCard accent="#7c3aed" sx={isLimitedAccess ? lockedCardSx : undefined}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                                <NotificationsActive sx={{ fontSize: 16, color: "#7c3aed" }} />
                                <CollectKpiLabel sx={{ mb: "0 !important" }}>
                                    Reminders sent
                                </CollectKpiLabel>
                            </Box>
                            <CollectKpiValue>
                                {isLoadingDunningEvents ? (
                                    <CircularProgress size={20} sx={{ color: colors.primary }} />
                                ) : (
                                    dunningEvents.filter((e) => e.status === "sent").length
                                )}
                            </CollectKpiValue>
                            <CollectKpiHint>
                                {dunningEvents.filter((e) => e.status === "failed").length > 0
                                    ? `${dunningEvents.filter((e) => e.status === "failed").length} failed · `
                                    : ""}
                                {dunningEvents.filter((e) => e.status === "skipped").length}{" "}
                                skipped · recent activity
                            </CollectKpiHint>
                            {/* Success rate */}
                            {!isLoadingDunningEvents && dunningEvents.length > 0 && (() => {
                                const sent = dunningEvents.filter((e) => e.status === "sent").length;
                                const failed = dunningEvents.filter((e) => e.status === "failed").length;
                                const total = sent + failed;
                                const rate = total > 0 ? Math.round((sent / total) * 100) : 100;
                                const isGood = rate >= 90;
                                return (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                                        {isGood
                                            ? <TrendingUp sx={{ fontSize: 13, color: colors.mainSecondary }} />
                                            : <TrendingDown sx={{ fontSize: 13, color: "#dc2626" }} />}
                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.72rem", fontWeight: 700, color: isGood ? colors.mainSecondary : "#dc2626" }}>
                                            {rate}% success rate
                                        </Typography>
                                    </Box>
                                );
                            })()}
                        </CollectKpiCard>
                    </Grid>
                </Grid>

                {/* ─── Limited-access message (blocks feature access) ─── */}
                {isLimitedAccess && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "stretch", sm: "center" },
                            gap: { xs: 1.5, sm: 2 },
                            p: { xs: 2, sm: 2.5 },
                            mb: 3,
                            borderRadius: "14px",
                            border: "1px solid rgba(220,38,38,0.25)",
                            backgroundColor: "rgba(220,38,38,0.06)",
                        }}
                    >
                        <WarningAmber sx={{ color: "#dc2626", fontSize: 26, flexShrink: 0 }} />
                        <Box flex={1} sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 800,
                                    fontSize: "0.95rem",
                                    color: "#b91c1c",
                                }}
                            >
                                Limited access, your subscription isn't active
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.83rem",
                                    color: "#7f1d1d",
                                    mt: 0.25,
                                }}
                            >
                                Update your billing information or subscription to
                                restore full access to your dashboard, invoices,
                                recovery tools and analytics.
                            </Typography>
                        </Box>
                        <CollectPrimaryButton
                            onClick={() => navigate("/collect/billing")}
                            sx={{
                                flexShrink: 0,
                                width: { xs: "100%", sm: "auto" },
                                backgroundColor: "#dc2626",
                                "&:hover": { backgroundColor: "#b91c1c" },
                            }}
                        >
                            Update billing
                        </CollectPrimaryButton>
                    </Box>
                )}

                {/* Everything below stays hidden until the subscription is settled. */}
                {!isLimitedAccess && (
                    <>
                        {/* ─── Recovery Impact scorecard ──────────────────────── */}
                        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                            <RecoveryImpactPanel businessId={activeBusinessId} />
                        </Box>
                        {/* ─── Autopilot reassurance summary ──────────────────── */}
                        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                            <AutopilotSummaryPanel businessId={activeBusinessId} />
                        </Box>
                        {/* ─── Action Center triage queue ─────────────────────── */}
                        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                            <ActionCenterPanel businessId={activeBusinessId} />
                        </Box>

                        {/* ─── More analytics & details (now its own page) ────────
                     The deeper breakdowns (collections trends, recovery funnel,
                     relationship safety, promises, live workflow) moved to a
                     dedicated Analytics page so the dashboard stays focused on
                     recovered / working / needs-me. Nothing was removed — every
                     one of those panels still renders at /collect/analytics. */}
                        <Box
                            onClick={() => navigate("/collect/analytics")}
                            role="button"
                            tabIndex={0}
                            sx={{
                                ...clickableKpiSx,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                flexWrap: "wrap",
                                mb: 3,
                                p: { xs: 2, sm: 2.5 },
                                borderRadius: "14px",
                                border: "1px solid rgba(15,23,42,0.08)",
                                backgroundColor: "#fff",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "10px",
                                        backgroundColor: "rgba(124,58,237,0.1)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Insights sx={{ fontSize: 20, color: "#7c3aed" }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 800,
                                            fontSize: "0.95rem",
                                            color: colors.primary,
                                        }}
                                    >
                                        Analytics &amp; details
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.8rem",
                                            color: "#6b7280",
                                        }}
                                    >
                                        Collections trends, recovery funnel, relationship
                                        safety, promises and the live workflow.
                                    </Typography>
                                </Box>
                            </Box>
                            <CollectGhostButton
                                endIcon={<OpenInNew fontSize="small" />}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    navigate("/collect/analytics");
                                }}
                            >
                                Open
                            </CollectGhostButton>
                        </Box>

                        {/* ─── What we did for you (activity feed) ────────────── */}
                        <Box sx={{ mb: 3 }}>
                            <ActivityFeedPanel businessId={activeBusinessId} />
                        </Box>

                        {/* ─── AR Aging ───────────────────────────────────────── */}
                        <CollectSectionCard>
                            <CollectSectionHeader>
                                <Box>
                                    <CollectSectionTitle>A/R Aging Snapshot</CollectSectionTitle>
                                    <CollectSectionSubtitle>
                                        How long your unpaid invoices have been outstanding,
                                        grouped the same way QuickBooks does it.
                                    </CollectSectionSubtitle>
                                </Box>
                                <Tooltip title="Refresh">
                                    <IconButton
                                        size="small"
                                        onClick={() => dispatch(fetchArAging(undefined))}
                                    >
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </CollectSectionHeader>

                            {isLoadingArAging ? (
                                <Box display="flex" justifyContent="center" py={4}>
                                    <CircularProgress sx={{ color: colors.primary }} />
                                </Box>
                            ) : arAging && arAging.buckets.length > 0 ? (
                                <Grid container spacing={2}>
                                    {arAging.buckets.map((bucket) => (
                                        <Grid item xs={6} sm={4} md={2.4} key={bucket.label}>
                                            <AgingBucketCard tone={AGING_TONE[bucket.label] || "warn"}>
                                                <AgingBucketLabel>
                                                    {AGING_LABELS[bucket.label] || bucket.label}
                                                </AgingBucketLabel>
                                                <AgingBucketValue>
                                                    {formatCurrency(bucket.total, currency)}
                                                </AgingBucketValue>
                                                <AgingBucketCount>
                                                    {bucket.count} invoice{bucket.count === 1 ? "" : "s"}
                                                </AgingBucketCount>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontSize: "0.7rem",
                                                        color: "#9ca3af",
                                                        marginTop: "6px",
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {AGING_DESCRIPTIONS[bucket.label] || ""}
                                                </Typography>
                                            </AgingBucketCard>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <CollectEmptyState>
                                    <CollectEmptyTitle>No receivables data yet</CollectEmptyTitle>
                                    <CollectEmptyHint>
                                        {isConnected
                                            ? "Run a sync to pull your invoices from QuickBooks."
                                            : "Connect QuickBooks to populate your aging report."}
                                    </CollectEmptyHint>
                                </CollectEmptyState>
                            )}
                        </CollectSectionCard>

                        <Grid container spacing={3}>
                            {/* ─── Top at-risk invoices (full width) ───────────── */}
                            <Grid item xs={12}>
                                <CollectSectionCard>
                                    <CollectSectionHeader>
                                        <Box>
                                            <CollectSectionTitle>Top At-Risk Invoices</CollectSectionTitle>
                                            <CollectSectionSubtitle>
                                                Your 5 highest-balance overdue invoices — act now to protect cash flow
                                            </CollectSectionSubtitle>
                                        </Box>
                                        <CollectGhostButton
                                            endIcon={<OpenInNew fontSize="small" />}
                                            onClick={() => navigate("/collect/invoices")}
                                        >
                                            See all
                                        </CollectGhostButton>
                                    </CollectSectionHeader>

                                    {isLoadingInvoices ? (
                                        <Box display="flex" justifyContent="center" py={4}>
                                            <CircularProgress sx={{ color: colors.primary }} />
                                        </Box>
                                    ) : invoices.length === 0 ? (
                                        <CollectEmptyState>
                                            <CollectEmptyTitle>No invoices yet</CollectEmptyTitle>
                                            <CollectEmptyHint>
                                                {isConnected
                                                    ? "Run a sync to import invoices from QuickBooks."
                                                    : "Connect QuickBooks to import invoices."}
                                            </CollectEmptyHint>
                                        </CollectEmptyState>
                                    ) : (() => {
                                        const today = new Date();
                                        const atRisk = invoices
                                            .filter((inv) => inv.status === "overdue" || (inv.balance > 0 && inv.dueDate && new Date(inv.dueDate) < today))
                                            .sort((a, b) => (b.balance || 0) - (a.balance || 0))
                                            .slice(0, 5);
                                        const display = atRisk.length > 0 ? atRisk : invoices.slice(0, 5);
                                        return (
                                            <Box sx={{ overflowX: "auto" }}>
                                                <Box sx={{ minWidth: 560 }}>
                                                    {/* Header row */}
                                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 110px 120px 110px", gap: 2, px: 1, mb: 1 }}>
                                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>Invoice / Customer</Typography>
                                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center" }}>Due</Typography>
                                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Balance</Typography>
                                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center" }}>Status</Typography>
                                                    </Box>
                                                    {display.map((inv) => {
                                                        const msOverdue = inv.dueDate ? today.getTime() - new Date(inv.dueDate).getTime() : 0;
                                                        const daysOverdue = Math.max(0, Math.floor(msOverdue / 86400000));
                                                        const isOverdue = inv.status === "overdue" || daysOverdue > 0;
                                                        return (
                                                            <Box
                                                                key={inv._id}
                                                                onClick={() => navigate(`/collect/invoices/${inv._id}`)}
                                                                sx={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: "1fr 110px 120px 110px",
                                                                    gap: 2,
                                                                    alignItems: "center",
                                                                    px: 1,
                                                                    py: 1.5,
                                                                    borderRadius: "8px",
                                                                    borderBottom: "1px solid rgba(15,23,42,0.04)",
                                                                    cursor: "pointer",
                                                                    "&:last-of-type": { borderBottom: "none" },
                                                                    "&:hover": { backgroundColor: "rgba(15,23,42,0.02)" },
                                                                    ...(isOverdue ? { borderLeft: "3px solid #dc2626", pl: 1.25 } : {}),
                                                                }}
                                                            >
                                                                <Box sx={{ minWidth: 0 }}>
                                                                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: "0.9rem", color: colors.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                        {inv.docNumber || `INV-${inv._id.slice(-6)}`}
                                                                    </Typography>
                                                                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.75rem", color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                        {(inv as any).customerDisplayName || inv.customer?.displayName || "Customer"}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ textAlign: "center" }}>
                                                                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.78rem", color: isOverdue ? "#dc2626" : "#374151", fontWeight: isOverdue ? 700 : 500 }}>
                                                                        {formatDate(inv.dueDate)}
                                                                    </Typography>
                                                                    {isOverdue && daysOverdue > 0 && (
                                                                        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.65rem", color: "#dc2626", fontWeight: 700 }}>
                                                                            {daysOverdue}d overdue
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: "0.95rem", color: isOverdue ? "#dc2626" : colors.primary, textAlign: "right" }}>
                                                                    {formatCurrency(inv.balance, inv.currency || currency)}
                                                                </Typography>
                                                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                                    <CollectStatusChip
                                                                        statusKey={inv.status as any}
                                                                        label={inv.status.replace("_", " ")}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        );
                                    })()}
                                </CollectSectionCard>
                            </Grid>

                            {/* ─── Recent reminder activity ───────────────────── */}
                            <Grid item xs={12} md={5}>
                                <CollectSectionCard>
                                    <CollectSectionHeader>
                                        <Box>
                                            <CollectSectionTitle>Recent Activity</CollectSectionTitle>
                                            <CollectSectionSubtitle>
                                                Reminders sent and skipped
                                            </CollectSectionSubtitle>
                                        </Box>
                                        <CollectGhostButton
                                            endIcon={<OpenInNew fontSize="small" />}
                                            onClick={() => navigate("/collect/activity")}
                                        >
                                            See all
                                        </CollectGhostButton>
                                    </CollectSectionHeader>

                                    {isLoadingDunningEvents ? (
                                        <Box display="flex" justifyContent="center" py={4}>
                                            <CircularProgress sx={{ color: colors.primary }} />
                                        </Box>
                                    ) : dunningEvents.length === 0 ? (
                                        <CollectEmptyState>
                                            <CollectEmptyTitle>No reminders yet</CollectEmptyTitle>
                                            <CollectEmptyHint>
                                                Once your campaigns are active, reminder activity will show
                                                here.
                                            </CollectEmptyHint>
                                        </CollectEmptyState>
                                    ) : (
                                        <Box>
                                            {dunningEvents.slice(0, 5).map((evt) => (
                                                <Box
                                                    key={evt._id}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: 1.5,
                                                        py: 1.25,
                                                        borderBottom: "1px solid rgba(15,23,42,0.04)",
                                                        "&:last-of-type": { borderBottom: "none" },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                                evt.status === "sent"
                                                                    ? "rgba(30,193,149,0.15)"
                                                                    : evt.status === "failed"
                                                                        ? "rgba(220,38,38,0.15)"
                                                                        : "rgba(107,114,128,0.15)",
                                                            color:
                                                                evt.status === "sent"
                                                                    ? "#0f7a5b"
                                                                    : evt.status === "failed"
                                                                        ? "#b91c1c"
                                                                        : "#6b7280",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <NotificationsActive fontSize="small" />
                                                    </Box>
                                                    <Box flex={1}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "'Montserrat', sans-serif",
                                                                fontWeight: 600,
                                                                fontSize: "0.85rem",
                                                                color: colors.primary,
                                                            }}
                                                        >
                                                            {evt.subject || `Reminder · ${evt.status}`}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "'Montserrat', sans-serif",
                                                                fontSize: "0.75rem",
                                                                color: "#6b7280",
                                                            }}
                                                        >
                                                            {evt.recipient ? `${evt.recipient} · ` : ""}
                                                            {timeAgo(evt.sentAt)} · {evt.trigger.toLowerCase()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </CollectSectionCard>
                            </Grid>
                        </Grid>
                    </>
                )}
            </CollectContentArea>

            {/* ─── KPI card drill-down modal ──────────────────────────── */}
            <KpiDetailModal
                metric={activeKpi}
                businessId={activeBusinessId}
                range={metricsRange}
                currency={currency}
                onClose={() => setActiveKpi(null)}
            />
        </CollectPageWrapper>
    );
};

export default CollectDashboard;
